import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface FetchData {
  cookies: {
    "authjs.csrf-token"?: string;
    [key: string]: string | undefined;
  };
  // Add other properties if your API returns more data
}

export const useFetchWithRetry = (
  url: string,
  userOptions = {},
  maxRetries = 2,
  interval = 200,
) => {
  const [data, setData] = useState<FetchData | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const options = useMemo(() => userOptions, [userOptions]);
  const csrfToken = "authjs.csrf-token";
  const attemptsRef = useRef(0);
  const isCancelledRef = useRef(false);

  useEffect(() => {
    const fetchData = async () => {
      while (attemptsRef.current < maxRetries && !isCancelledRef.current) {
        try {
          const response = await fetch(url, options);
          const result: FetchData = await response.json();
          if (result.cookies && result.cookies[csrfToken]) {
            setData(result);
            break; // Exit loop on successful fetch
          }
        } catch (err) {
          setError(err as Error);
        }

        attemptsRef.current += 1;
        await new Promise((resolve) => setTimeout(resolve, interval));
      }
    };

    fetchData();

    return () => {
      isCancelledRef.current = true;
    };
  }, [url, options, maxRetries, interval]);

  return { data, error };
};

interface UseFetchOptions<T> {
  url: string;
  dependsOn?: any[];
  initialData: T;
}

export function useFetch<T>({
  url,
  dependsOn = [],
  initialData,
}: UseFetchOptions<T>) {
  const [data, setData] = useState<T>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(() => {
    const controller = new AbortController();
    const { signal } = controller;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(url, { credentials: "include", signal });
        if (!res.ok) throw new Error("Erro na requisição");
        const json = await res.json();
        setData(json);
      } catch (err: any) {
        if (err?.name !== "AbortError")
          setError(err?.message ?? "Erro desconhecido");
      } finally {
        setLoading(false);
      }
    })();

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  useEffect(fetchData, [fetchData]);

  return { data, loading, error } as const;
}
